import React, { useEffect, useRef, useState, CSSProperties } from "react";
import { components } from "react-select";
import AsyncSelect from "react-select/async";

import { MODEL_OPTIONS, ModelOptionsType } from "../ModelBadge";
import { getLoadAutocompleteOptions } from "../../helpers/api";
import { VALID_GREEN } from "../../constants";
import { overlayStyles } from "../Modal";

function MenuList({ selectProps, children, ...props }: any) {
  const MenuListComponent = components.MenuList as any;
  return (
    <MenuListComponent {...props}>
      {children}
      <div className="text-center py-2">
        <small>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              selectProps.setShowAdvancedSearch(true);
            }}
          >
            Use Advanced Search
          </a>
        </small>
      </div>
    </MenuListComponent>
  );
}

function NoOptionsMessage({ selectProps, children, ...props }: any) {
  const NoOptionsMessageComponent = components.NoOptionsMessage as any;
  return (
    <NoOptionsMessageComponent {...props}>
      {selectProps.inputValue === ""
        ? "Please enter a search term..."
        : children}
    </NoOptionsMessageComponent>
  );
}

interface AdvancedSearchProps {
  setShowAdvancedSearch: (show: boolean) => void;
  advancedSearchComponent: React.ComponentType<{
    customerId: string | number;
    role: string;
    onChange: (value: any) => void;
  }>;
  customerId: string | number;
  role: string;
  onChange: (value: any) => void;
}

function AdvancedSearch({
  setShowAdvancedSearch,
  advancedSearchComponent: AdvancedSearchComponent,
  customerId,
  role,
  onChange,
}: AdvancedSearchProps) {
  return (
    <div
      style={overlayStyles}
      className="d-flex align-items-center justify-content-center"
    >
      <div className="modal d-block" tabIndex={-1} role="dialog">
        <div
          className="modal-dialog"
          role="document"
          style={{
            maxWidth: "none",
            height: "100vh",
            padding: "0 1rem 1rem",
            boxSizing: "border-box",
          }}
        >
          <div
            className="modal-content"
            style={{ height: "calc(100% - 100px)" }}
          >
            <div className="modal-header">
              <h5 className="modal-title">Advanced Search</h5>
              <button
                type="button"
                className="close"
                tabIndex={-1}
                onClick={() => setShowAdvancedSearch(false)}
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body" style={{ overflowY: "scroll" }}>
              <AdvancedSearchComponent
                customerId={customerId}
                role={role}
                onChange={(value) => {
                  onChange(value);
                  setShowAdvancedSearch(false);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface OptionType {
  value: any;
  label: string;
}

interface AutocompleteProps {
  customerId: string | number;
  role: string;
  onChange: (value: OptionType | OptionType[] | null) => void;
  url: string;
  autoFocus?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
  isMulti?: boolean;
  valid?: boolean;
  validate?: boolean;
  value?: OptionType | OptionType[];
  showAll?: boolean;
  model?: keyof ModelOptionsType;
  advancedSearchComponent?: React.ComponentType<{
    customerId: string | number;
    role: string;
    onChange: (value: any) => void;
  }>;
  [key: string]: any; // for extraProps
}

const Autocomplete: React.FC<AutocompleteProps> = ({
  customerId,
  role,
  onChange,
  url,
  autoFocus = false,
  isDisabled = false,
  isClearable = false,
  isMulti = false,
  valid = true,
  validate = false,
  value = undefined,
  showAll = false,
  model = null,
  advancedSearchComponent = null,
  ...extraProps
}) => {
  // track whether component is mounted, to cancel loadOptions callback
  const isMounted = useRef(false);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  });

  const loadOptions = getLoadAutocompleteOptions({ url, customerId, showAll });
  const styles: CSSProperties = {};
  if (validate && !valid) styles.borderColor = "red";
  else if (validate) styles.borderColor = VALID_GREEN;

  const selectComponents = advancedSearchComponent
    ? { MenuList, NoOptionsMessage }
    : { NoOptionsMessage };

  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

  return (
    <div>
      {showAdvancedSearch && advancedSearchComponent && (
        <AdvancedSearch
          setShowAdvancedSearch={setShowAdvancedSearch}
          advancedSearchComponent={advancedSearchComponent}
          customerId={customerId}
          role={role}
          onChange={(_value: any) => {
            if (!isMulti) {
              onChange(_value);
            } else if (!value) {
              onChange([_value]);
            } else {
              onChange([...(value as OptionType[]), _value]);
            }
          }}
        />
      )}
      <AsyncSelect
        autoFocus={autoFocus}
        isDisabled={isDisabled}
        isMulti={isMulti}
        onChange={onChange}
        isClearable={isClearable}
        loadOptions={(
          inputValue: string,
          callback: (options: OptionType[]) => void,
        ) => loadOptions(inputValue, callback, isMounted.current)}
        value={value}
        styles={{
          control: (base: any) => ({ ...base, ...styles }),
          multiValue: (provided: any) => {
            if (!model) return provided;
            const options = MODEL_OPTIONS[model];
            const newStyles: CSSProperties = {
              backgroundColor: options.background,
            };
            if (options.className === "text-light") newStyles.color = "white";
            return { ...provided, ...newStyles };
          },
          multiValueLabel: (provided: any) => {
            if (!model) return provided;
            const options = MODEL_OPTIONS[model];
            const newStyles: CSSProperties = {};
            if (options.className === "text-light") newStyles.color = "white";
            return { ...provided, ...newStyles };
          },
        }}
        components={selectComponents}
        setShowAdvancedSearch={setShowAdvancedSearch}
        {...extraProps}
      />
    </div>
  );
};

export default Autocomplete;
